import { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/layouts/MainLayout';
import { Container, Alert, Card, PageHeader } from '@/components/ui';
import { CargoCountChart } from '@/components/cargoCount';
import { useCargoCountStore } from '@/store/cargoCountStore';
import type { CargoCountResult } from '@/types/cargoCount.types';

export function CargoCountPage() {
  const [hasRequestedInitialData, setHasRequestedInitialData] = useState(false);
  const { data, loading, error, fetchCargoCount } = useCargoCountStore();

  // Fetch data on mount
  useEffect(() => {
    setHasRequestedInitialData(true);
    fetchCargoCount();
  }, [fetchCargoCount]);

  const showLoadingSkeleton = loading || (!hasRequestedInitialData && data.length === 0 && !error);

  // Group data by area
  const cargoCountByArea = useMemo(() => {
    const grouped: { [key: string]: CargoCountResult[] } = {};
    data.forEach((item) => {
      if (!grouped[item.area]) {
        grouped[item.area] = [];
      }
      grouped[item.area].push(item);
    });
    return grouped;
  }, [data]);

  return (
    <MainLayout>
      <Container className="pb-8">
        <div className="mb-6">
          <PageHeader title="Cargo Count VLCC" />
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error" title="Error Loading Data" className="mb-6">
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {showLoadingSkeleton && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse overflow-hidden p-0">
                <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                  <div className="h-6 w-16 rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="flex items-center gap-3">
                    {[1, 2, 3].map((legend) => (
                      <div key={legend} className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full bg-gray-200 dark:bg-gray-700" />
                        <div className="h-3 w-12 rounded bg-gray-200 dark:bg-gray-700" />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4 px-6 py-4">
                  {[1, 2, 3, 4, 5].map((row) => (
                    <div key={row} className="flex items-center gap-3">
                      <div className="h-4 w-[82px] min-w-[82px] flex-shrink-0 rounded bg-gray-200 dark:bg-gray-700" />
                      <div className="flex flex-1 items-center gap-2">
                        <div className="h-3 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="h-3 w-1/4 rounded bg-gray-200 dark:bg-gray-700" />
                        <div className="h-3 w-1/5 rounded bg-gray-200 dark:bg-gray-700" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex px-6 pb-6 pt-2">
                  <div className="w-[82px] min-w-[82px] flex-shrink-0" />
                  <div className="flex flex-1 justify-between border-t border-gray-200 pt-2 dark:border-gray-700">
                    {[1, 2, 3, 4, 5, 6].map((tick) => (
                      <div key={tick} className="h-3 w-6 rounded bg-gray-200 dark:bg-gray-700" />
                    ))}
                  </div>
                </div>
              </Card>
            ))}

            <Card className="animate-pulse p-6">
              <div className="space-y-3">
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-4 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            </Card>
          </div>
        )}

        {/* Charts Grid */}
        {!showLoadingSkeleton && data.length > 0 && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {Object.entries(cargoCountByArea).map(([area, areaData]) => (
              <CargoCountChart key={area} header={area} cargoCount={areaData} />
            ))}

            {/* Info Card */}
            <Card className="flex items-center justify-center p-6 text-base text-gray-700 dark:text-gray-300 lg:text-lg">
              <p>
                Statistics include CoA, CVS and Spot Cargoes that are fully fixed or on subs. The
                expected volumes are automatically generated from the average historical volume over
                the past 6 months and adjusted for seasonal fluctuation based on historical volume.
              </p>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!showLoadingSkeleton && data.length === 0 && !error && (
          <div className="py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No cargo data available
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Cargo count data will appear here once available.
            </p>
          </div>
        )}
      </Container>
    </MainLayout>
  );
}
